/**
 * @fileoverview Quick-action buttons for common AI assistant tasks.
 * @module @firesquirrel/ai-chatbot
 *
 * Responsibilities:
 * - Render the set of quick actions (data report, dashboard, document
 *   template) with labels/descriptions adapted to the current `appContext`
 *   (tenant vs system).
 * - Report a selected action back to the caller via `onSelectAction`.
 *
 * Dependencies:
 * - React (peer), lucide-react icons.
 * - `useAIChat()` from `../contexts/AIChatContext` — `appContext` only.
 *
 * Architecture:
 * - Presentational; the caller (`<AIChatbot>`) owns what selection means
 *   (starts a report-creation flow through the chat context).
 *
 * Notes:
 * - Copy differs between 'system' and tenant contexts — descriptions and
 *   prompt messages are context-aware.
 */

import { FileText, BarChart3, LayoutDashboard } from 'lucide-react';
import { useAIChat } from '../contexts/AIChatContext';

/**
 * @contract QuickAction
 * Purpose: describes one quick-action tile the assistant can start.
 * Fields:  id, icon, label, description, reportType
 *          ('report'|'dashboard'|'document'), promptMessage (seeded
 *          assistant prompt for the flow).
 * Rules:   `reportType` is the discriminator the host uses to route the
 *          creation flow; `promptMessage` is shown/sent as the starting
 *          message.
 * Sync:    none — local display contract.
 */
interface QuickAction {
  id: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  description: string;
  reportType: 'report' | 'dashboard' | 'document';
  promptMessage: string;
}

/**
 * @contract AIQuickActionsProps
 * Purpose: props for `<AIQuickActions>`.
 * Fields:  selectedAction — id of the currently selected action (optional);
 *          onSelectAction — callback receiving the chosen `QuickAction`.
 * Rules:   `onSelectAction` is invoked only on tile click; `selectedAction`
 *          only affects styling.
 * Sync:    none — presentational props only.
 */
interface AIQuickActionsProps {
  selectedAction?: string;
  onSelectAction?: (action: QuickAction) => void;
}

/**
 * @component AIQuickActions
 * Purpose:     Renders the quick-action tile list for starting report,
 *              dashboard, or document-template creation flows.
 * Props:       selectedAction?, onSelectAction? — see
 *              `@contract AIQuickActionsProps`.
 * Behavior:    Static tile list; labels/descriptions/prompts switch on
 *              `appContext === 'system'`; click forwards the action object
 *              to `onSelectAction`.
 * Data Source: `appContext` from `useAIChat()` (in-memory `AIChatContext`
 *              state); no fetch/apiClient calls.
 * Constraints: no direct API calls; selection semantics belong to the
 *              parent.
 * Performance: trivial — renders three tiles.
 */
export default function AIQuickActions({ selectedAction, onSelectAction }: AIQuickActionsProps) {
  const { appContext } = useAIChat();
  const isSystem = appContext === 'system';

  const quickActions: QuickAction[] = [
    {
      id: 'data-report',
      icon: FileText,
      label: 'Data Report',
      description: isSystem ? 'Create a system-level report template' : 'Create a tabular data report',
      reportType: 'report',
      promptMessage: isSystem
        ? "I'll help you create a system-level data report template for departments! Describe the report structure — for example: \"Weekly inspection activity by inspector\" or \"Monthly violation summary by type\". This template will be available to all departments."
        : "I'll help you create a data report! Describe what data you'd like to see — for example: \"Show me open cases by inspector for the last 30 days\" or \"List all inspections by status and type\".",
    },
    {
      id: 'dashboard',
      icon: LayoutDashboard,
      label: 'Dashboard',
      description: isSystem ? 'Create a system-level dashboard template' : 'Build an analytics dashboard',
      reportType: 'dashboard',
      promptMessage: isSystem
        ? "I'll help you create a system-level dashboard template for departments! Describe what metrics departments should track — for example: \"Inspection completion rate by district\" or \"Violation trends over 6 months\". Departments can adopt this template."
        : "I'll help you build a dashboard! Tell me what metrics or charts you'd like — for example: \"Cases by status over time\" or \"Inspection completion rate by department\".",
    },
    {
      id: 'document-template',
      icon: BarChart3,
      label: 'Document Template',
      description: isSystem ? 'Import from PDF or create from scratch' : 'Import a template from an existing PDF',
      reportType: 'document',
      promptMessage: isSystem
        ? "I can help you create a system-level document template! You have two options:\n\n1. **Upload a PDF** of an existing form/template, and I'll analyze its structure to generate a template\n2. **Build from scratch** by describing the document sections you need\n\nWhich would you like to do?"
        : "I can help you create a document template! You have two options:\n\n1. **Upload a PDF** of your existing inspection report or form, and I'll analyze its structure and suggest field mappings\n2. **Build from scratch** by telling me what sections and fields you need\n\nWhich would you prefer?",
    },
  ];

  return (
    <div className="grid grid-cols-1 gap-2">
      {quickActions.map((action) => {
        const Icon = action.icon;
        const isSelected = selectedAction === action.id;
        return (
          <button
            key={action.id}
            onClick={() => onSelectAction?.(action)}
            className={`flex items-start gap-3 p-3 border rounded-lg transition-all text-left group ${
              isSelected
                ? 'bg-blue-50 border-blue-500 shadow-xs'
                : 'bg-white border-gray-200 hover:border-blue-500 hover:bg-blue-50'
            }`}
          >
            <div className={`shrink-0 w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
              isSelected
                ? 'bg-blue-200'
                : 'bg-blue-100 group-hover:bg-blue-200'
            }`}>
              <Icon className="w-5 h-5 text-blue-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className={`text-sm font-semibold transition-colors ${
                isSelected
                  ? 'text-blue-700'
                  : 'text-gray-900 group-hover:text-blue-700'
              }`}>
                {action.label}
              </h4>
              <p className="text-xs text-gray-600 mt-0.5">
                {action.description}
              </p>
            </div>
          </button>
        );
      })}
    </div>
  );
}
